import AppLayout from "@/components/layout/AppLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClientProviderEvaluationForm } from "@/components/provider-evaluation/ClientProviderEvaluationForm";
import { ClientProviderEvaluationsView } from "@/components/provider-evaluation/ClientProviderEvaluationsView";
import { AdminAnalytics } from "@/components/provider-evaluation/AdminAnalytics";
import { EvaluationMitigations } from "@/components/provider-evaluation/EvaluationMitigations";
import { useUserRoles } from "@/hooks/useUserRoles";
import { BarChart3, Shield, FileText, Star, Lock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const ProviderEvaluationsPage = () => {
  const { isAdmin, hasRole } = useUserRoles();
  const { t } = useLanguage();
  const { isEnabled } = useModuleAccess();

  // Check if user is a guard (guards shouldn't access this)
  const isGuard = hasRole ? hasRole('guard') : false;

  // Check if module is enabled
  if (!isEnabled("providerEvaluations")) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center p-6">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-muted p-3 rounded-full">
                  <Lock className="h-8 w-8 text-muted-foreground" />
                </div>
              </div>
              <CardTitle>{t("moduleNotAvailable") || "Module Not Available"}</CardTitle>
              <CardDescription>
                {t("providerEvaluationsModuleDisabled") || "The Provider Evaluations module is not enabled for your account. Please contact your administrator."}
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </AppLayout>
    );
  }

  // Block guards from accessing provider evaluations
  if (isGuard) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center p-6">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-destructive/10 p-3 rounded-full">
                  <Lock className="h-8 w-8 text-destructive" />
                </div>
              </div>
              <CardTitle>{t("accessDenied") || "Access Denied"}</CardTitle>
              <CardDescription>
                {t("providerEvaluationsNotAvailableForGuards") || "Provider Evaluations are only accessible to clients and administrators."}
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <div className="container mx-auto py-6">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Star className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold">{t("providerEvaluations")}</h1>
            </div>
            <p className="text-muted-foreground">
              {t("providerEvaluationsDesc")}
            </p>
          </div>

          {isAdmin ? (
            <Tabs defaultValue="analytics" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="analytics" className="gap-2">
                  <BarChart3 className="h-4 w-4" />
                  {t("analytics")}
                </TabsTrigger>
                <TabsTrigger value="mitigations" className="gap-2">
                  <Shield className="h-4 w-4" />
                  {t("mitigations")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="analytics">
                <AdminAnalytics />
              </TabsContent>

              <TabsContent value="mitigations">
                <EvaluationMitigations />
              </TabsContent>
            </Tabs>
          ) : (
            <Tabs defaultValue="form" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="form" className="gap-2">
                  <Star className="h-4 w-4" />
                  {t("newEvaluation")}
                </TabsTrigger>
                <TabsTrigger value="history" className="gap-2">
                  <FileText className="h-4 w-4" />
                  {t("history")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="form">
                <ClientProviderEvaluationForm />
              </TabsContent>

              <TabsContent value="history">
                <ClientProviderEvaluationsView />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default ProviderEvaluationsPage;
